/**
 * Deterministic randomness.
 *
 * The server sends no per-zombie identity (docs/api.md § 1.4), so every viewer has to *derive* the
 * same zombie from the siege id and the zombie's ordinal. That only works if the generator is
 * bit-for-bit reproducible, which rules out `Math.random` and rules out anything whose output
 * depends on a floating-point detail. `mulberry32` is 32-bit integer arithmetic via `Math.imul`,
 * so it produces the same stream in every engine.
 */

/** A pure `() => number` in `[0, 1)`. Passing the function around keeps call sites testable. */
export type Rng = () => number

export function mulberry32(seed: number): Rng {
  let a = seed | 0
  return () => {
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/**
 * FNV-1a over UTF-16 code units, returned as an unsigned 32-bit integer.
 *
 * Code units rather than UTF-8 bytes because the only inputs are `sid + ":" + ordinal` — a hex id
 * and a decimal number, both pure ASCII, where the two agree. Keeping it to code units means no
 * `TextEncoder` and no allocation on a path the client runs once per zombie.
 */
export function fnv1a32(str: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

/** Uniform in `[a, b)`. */
export function range(rng: Rng, a: number, b: number): number {
  return a + rng() * (b - a)
}

export interface Weighted {
  weight: number
}

/**
 * Weighted choice over anything carrying a `weight`. Non-positive weights are skipped; an empty
 * or all-zero list throws, because a silent `undefined` skin would surface much later as a
 * character with no texture.
 */
export function pick<T extends Weighted>(rng: Rng, weights: readonly T[]): T {
  let total = 0
  for (let i = 0; i < weights.length; i++) {
    const w = weights[i]!.weight
    if (w > 0) total += w
  }
  if (total <= 0) throw new Error('pick() needs at least one entry with a positive weight')
  let r = rng() * total
  // `chosen` is assigned on the first positive-weight entry, and `total > 0` guarantees one
  // exists; carrying it out of the loop rather than returning inside means floating-point drift
  // on the final comparison lands on the last candidate instead of falling off the end.
  let chosen: T | undefined
  for (let i = 0; i < weights.length; i++) {
    const entry = weights[i]!
    if (entry.weight <= 0) continue
    chosen = entry
    r -= entry.weight
    if (r < 0) break
  }
  return chosen as T
}
